import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { store } from '../redux/store';
import { setAuth, clearAuth } from '../redux/slices/authSlice';
import type { PaymentPageTrackAction } from '../types/paymentPageEvents';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _retryRefresh?: boolean;
};


const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
});

// Add request interceptor: attach Bearer token and prevent caching
api.interceptors.request.use((config) => {
  const isAuthEndpoint = config.url?.startsWith('/auth/login') || config.url?.startsWith('/auth/refresh') || config.url?.startsWith('/auth/users');
  if (!isAuthEndpoint) {
    const state = store.getState();
    const token = state.auth.accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  const method = config.method?.toLowerCase();
  if (method && ['post', 'patch', 'put', 'delete'].includes(method)) {
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';
    config.headers['X-Request-ID'] = `${Date.now()}-${Math.random()}`;
  }
  if (method === 'get') {
    config.params = { ...config.params, _t: Date.now() };
  }
  return config;
});

// Add response interceptor: 401 -> try refresh then retry; else clear auth and redirect
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 304) {
      const originalRequest = error.config as RetryableRequestConfig | undefined;
      if (originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        originalRequest.params = { ...originalRequest.params, _t: Date.now() };
        return api(originalRequest);
      }
    }

    const originalRequest = error.config as RetryableRequestConfig | undefined;
    const isRefreshRequest = originalRequest?.url?.includes?.('/auth/refresh');
    if (error.response?.status === 401 && originalRequest && !originalRequest._retryRefresh && !isRefreshRequest) {
      const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
      const publishPrefix = (process.env.REACT_APP_PUBLISH_PATH_PREFIX || 'pago').replace(/^\/+|\/+$/g, '') || 'pago';
      const isPublicPublishedPage = pathname.startsWith(`/${publishPrefix}/`);
      if (isPublicPublishedPage) {
        return Promise.reject(error);
      }
      originalRequest._retryRefresh = true;
      const refreshToken = store.getState().auth.refreshToken;
      if (refreshToken) {
        try {
          const authResponse = await authApi.refresh(refreshToken);
          store.dispatch(setAuth(authResponse));
          originalRequest.headers.Authorization = `Bearer ${authResponse.access_token}`;
          return api(originalRequest);
        } catch {
          store.dispatch(clearAuth());
          window.location.href = '/login';
          return Promise.reject(error);
        }
      }
      store.dispatch(clearAuth());
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API (no Bearer required for login/refresh)
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  user: { id: number; email: string };
}

export const authApi = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    return response.data;
  },

  refresh: async (refresh_token: string): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/refresh', { refresh_token });
    return response.data;
  },

  logout: async (): Promise<void> => {
    try {
      await api.get('/auth/logout');
    } finally {
      store.dispatch(clearAuth());
    }
  },
};

export type ValidationType = 'required' | 'required_to_copy' | 'min_length' | 'max_length' | 'pattern' | 'min' | 'max' | 'email' | 'url' | 'custom' | 'db_value_exists';

export interface Validation {
  id: number;
  name: string;
  description?: string;
  type: ValidationType;
  message?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- validation config is a free-form bag
  config?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface InputsGroupInputItem {
  label?: string;
  name?: string;
  placeholder?: string;
  value?: string;
  inputType?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'date' | 'time' | 'datetime-local' | 'month' | 'week' | 'search' | 'color';
  validationIds?: number[];
}

export interface FormElement {
  id?: number;
  type: 'input' | 'select' | 'button' | 'text' | 'image' | 'list' | 'text_with_value' | 'timer' | 'copy_section' | 'link' | 'upload_file' | 'step_progress_bar' | 'modal' | 'notification_message' | 'container' | 'inputs_group' | 'processing_status' | 'slideshow' | 'loader' | 'svg' | 'qr_code';
  label?: string;
  name?: string;
  placeholder?: string;
  value?: string;
  className?: string;
  htmlId?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- builder styles mix CSS + custom keys
  styles?: Record<string, any> & {
    validationTriggers?: string[]; // For inputs: ['onChange', 'onBlur', 'onInput', etc.]
    triggerValidationsFor?: string[]; // For buttons: array of input element names to validate
    listItems?: string[]; // For lists: array of list item strings
    subtitle?: string; // For lists: subtitle text
    textValue?: string; // For text_with_value: the text to display on the left
    textWithValueValueFormat?: 'none' | 'number' | 'phone'; // For text_with_value: format value (right side) for display
    initialTime?: string; // For timer: initial time in MM:SS format (when time source is static)
    timerTimeSource?: 'static' | 'variable' | 'url'; // For timer: static MM:SS, DB variable (e.g. expires_at), or URL query param
    timerVariableName?: string; // For timer: transaction column name for countdown target (e.g. expires_at)
    timerUrlParamKey?: string; // For timer (when timerTimeSource === 'url'): URL query param for countdown target
    timerOnExpiry?: 'show_00' | 'show_error_page' | 'show_link'; // For timer: when countdown reaches zero
    timerPosition?: 'inline' | 'top-left' | 'top' | 'top-right'; // For timer: placement on form
    timerSvgBefore?: string; // For timer: SVG code to show before the time
    linkElementIdAfterExpiry?: number; // For timer (show_link): element id of link to show when timer expires
    copyIcon?: string; // For copy_section: SVG code for copy icon
    copyDoneIcon?: string; // For copy_section: SVG code for copy done icon
    hideCopyIcons?: boolean; // For copy_section: hide copy and copy done icons
    copySuccessMessage?: string; // For copy_section: success message template (e.g., '{copied_value} copied' or 'copied')
    copySubtitle?: string; // For copy_section: subtitle text displayed before copy button
    copySectionValueSource?: 'db' | 'url'; // For copy_section: where the copy value comes from. 'db' (default) uses element.value with {variable} substitution from the transaction; 'url' reads it from a URL query param.
    copySectionUrlParamKey?: string; // For copy_section (when copySectionValueSource === 'url'): the URL query param name to read the copy value from
    copySectionValueFormat?: 'none' | 'number' | 'phone'; // For copy_section: format value for display; phone shows (XXXX) XXX-XX-X, copy is digits only
    copySectionValueNumberLocale?: string; // e.g. 'de-DE' for 250.000,00
    copySectionValueNumberMinFractionDigits?: number;
    copySectionValueNumberMaxFractionDigits?: number;
    copySectionHideFirstCharacters?: number; // For copy_section: hide (truncate) first N characters of value on display and copy
    /** Generic value source for element.value (input, link, qr_code, text_with_value, etc.). Also used as fallback for text/copy_section. */
    valueSource?: 'db' | 'url';
    /** URL query param key when valueSource === 'url'. */
    urlParamKey?: string;
    textValueSource?: 'db' | 'url'; // For text: where the value comes from. 'db' (default) uses element.value with {variable} substitution from the transaction; 'url' reads it from a URL query param.
    textUrlParamKey?: string; // For text (when textValueSource === 'url'): the URL query param name to read the value from
    /** For text_with_value left-side text (styles.textValue). */
    sideTextValueSource?: 'db' | 'url';
    sideTextUrlParamKey?: string;
    textFormat?: 'none' | 'number'; // For text: format value as number
    textNumberLocale?: string; // e.g. 'de-DE'
    textNumberMinFractionDigits?: number;
    textNumberMaxFractionDigits?: number;
    showStep?: number; // For buttons: step ID to navigate to after click (step-by-step mode)
    listenVisibilityChange?: boolean; // For buttons: re-enable button when user returns to tab (visibilitychange/focus)
    disabledClickMessage?: string; // For buttons: message to show when user clicks disabled button (red popup)
    linkIcon?: string; // For link: SVG code to display before the link
    hideLinkText?: boolean; // For link: show icon only, hide text
    linkTarget?: string; // For link: _blank, _self, _parent, _top, or custom frame name
    linkOnClick?: string; // For link: JavaScript code for onclick (event is available as 'event')
    linkHidden?: boolean; // For link: hide link until revealed (e.g. after timer expires)
    linkAlign?: 'left' | 'center' | 'right'; // For link: alignment of the link block
    uploadSubtitle?: string; // For upload_file: subtitle text displayed below title
    hideLabels?: boolean; // For step_progress_bar: hide step titles/labels
    hiddenStepIndices?: number[]; // For step_progress_bar: 0-based indices of steps to hide from the bar
    modalElements?: FormElement[]; // For modal: nested elements inside the modal
    notificationIcon?: string; // For notification_message: SVG code for icon before title
    notificationDescription?: string; // For notification_message: description text (when notificationDescriptionType is 'text')
    notificationDescriptionType?: 'text' | 'list'; // For notification_message: type of description
    notificationListItems?: string[]; // For notification_message: list items (when notificationDescriptionType is 'list')
    processingTitle?: string; // For processing_status: title on the left (e.g. "Estado del Pago")
    processingStatusSvg?: string; // For processing_status: SVG code for rotating spinner icon
    statusTitle?: string; // For processing_status: text on the right of the SVG (e.g. "En Curso")
    containerElements?: FormElement[]; // For container: nested elements inside the container
    inputType?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'date' | 'time' | 'datetime-local' | 'month' | 'week' | 'search' | 'color'; // For input: HTML input type
    hideInputIfValidationCorrect?: boolean; // For input: hide on init when API variable value passes selected validations
    selectOptions?: Array<{ value: string; label: string }>; // For select: dropdown options
    inputsGroupInputs?: InputsGroupInputItem[]; // For inputs_group: list of inputs (one shown at a time)
    inputsGroupButtonId?: number | null; // For inputs_group: related button element id that advances to next input
    inputsGroupAnimation?: 'fade' | 'none'; // For inputs_group: transition type
    inputsGroupAnimationDuration?: number; // For inputs_group: transition duration in ms
    hidden?: boolean; // When true, element is hidden on the form (show/hide switcher)
    showByUrlParamKey?: string; // URL query param name; if set, element is shown only when param value matches showByUrlParamValue
    showByUrlParamValue?: string; // Allowed value(s) separated by | (e.g. 'true' or 'true|yes'); element shown when URL param equals one of these (or contains one if showByUrlParamContains)
    showByUrlParamContains?: boolean; // When true, show if URL param value contains the configured value (substring match)
    showByVariableSource?: 'db' | 'url'; // Where showByVariableKey is read from (default db = transaction)
    showByVariableKey?: string; // Transaction variable/column name or URL param key; if set, element shown only when value matches showByVariableValue
    showByVariableValue?: string; // Allowed value(s) separated by |; element shown when value equals one (or contains one if showByVariableContains)
    showByVariableContains?: boolean; // When true, show if value contains the configured value
    hideByVariableSource?: 'db' | 'url'; // Where hideByVariableKey is read from (default db = transaction)
    hideByVariableKey?: string; // Transaction variable or URL param key; if set, element is hidden when value matches hideByVariableValue
    hideByVariableValue?: string; // Value(s) separated by |; element hidden when value equals one (or contains one if hideByVariableContains)
    hideByVariableContains?: boolean; // When true, hide if value contains the configured value
    /** Payment page socket analytics action (copy_section / button) when paymentPageEventsEnabled. */
    paymentPageTrackAction?: PaymentPageTrackAction;
    slideshowPrevSvg?: string; // For slideshow: SVG code for prev button
    slideshowNextSvg?: string; // For slideshow: SVG code for next button
    slideshowButtonBackground?: string; // For slideshow: button background color
    loaderColor?: string; // For loader: spinner color (e.g. rgb(11, 111, 199))
    loaderWidth?: string; // For loader: width (e.g. 50px)
    loaderHeight?: string; // For loader: height (e.g. 50px)
    svgCode?: string; // For svg: raw SVG markup
    svgAlign?: 'left' | 'center' | 'right'; // For svg: alignment
    qrEcLevel?: 'L' | 'M' | 'Q' | 'H'; // For qr_code: error correction
    qrSize?: number; // For qr_code: pixel size (default 180)
    qrQuietZone?: number; // For qr_code: quiet zone pixels
    qrBgColor?: string;
    qrFgColor?: string;
    qrLogoImageUrl?: string; // For qr_code: logo image URL (supports {variables})
    qrLogoWidth?: number;
    qrLogoHeight?: number;
    qrLogoOpacity?: number;
    qrLogoPadding?: number;
    qrLogoPaddingStyle?: 'square' | 'circle';
    qrRemoveQrCodeBehindLogo?: boolean;
    qrStyle?: 'squares' | 'dots' | 'fluid';
    qrEnableCORS?: boolean; // For qr_code: logo from other origin
  };
  order?: number;
  stepId?: number | string;
  validations?: Validation[];
  validationIds?: number[]; // Used when validations not populated (e.g. nested container elements from API)
}

export interface StepVisibilityCondition {
  column: string;
  value: string;
}

export interface StepVisibilityConditions {
  showConditions?: StepVisibilityCondition[];
  hideConditions?: StepVisibilityCondition[];
  showMatchMode?: 'all' | 'any';
  hideMatchMode?: 'all' | 'any';
}

export interface FormStep {
  id?: number;
  /** Stable client-side identity for unsaved steps (UUID). Survives reorder; binds elements via stepId. */
  clientKey?: string;
  title: string;
  description?: string;
  order?: number;
  stepStyleId?: number | null;
  visibilityConditions?: StepVisibilityConditions | null;
  elements?: FormElement[];
}

export interface Form {
  id: number;
  name: string;
  description?: string;
  previewImageUrl?: string | null;
  formStyleId?: number;
  bodyStyleId?: number;
  messagesStyleId?: number | null;
  customCss?: string;
  customJs?: string;
  /** structured = element builder; html = single-file HTML source */
  formMode?: 'structured' | 'html';
  htmlSource?: string | null;
  /** HTML mode: wait for socket `service` and show matching variant (empty serviceId = default) */
  htmlCheckService?: boolean;
  htmlServiceVariants?: Array<{
    id?: string;
    /** Empty string = default fallback when socket has no/unmatched service */
    serviceId: string;
    htmlSource: string;
    label?: string;
  }> | null;
  styles?: {
    viewMode?: 'single_page' | 'step_by_step';
    stepAnimation?: 'fade' | 'slide' | 'none';
    showBackButton?: boolean; // For step_by_step mode: show back button
    backButtonIcon?: string; // SVG code for back button icon
    backButtonText?: string; // Optional text for back button
    hideFormName?: boolean; // Hide form name in preview
    maxWidth?: string; // Max width of the form preview (e.g., '800px', '100%', '1200px')
    bodyPaddingY?: string | number; // Body vertical padding (e.g. '32px' or theme spacing 4)
    bodyPaddingX?: string | number; // Body horizontal padding (e.g. '16px' or theme spacing 2)
    bodyPaddingYMobile?: string | number; // Body vertical padding on mobile (xs breakpoint)
    bodyPaddingXMobile?: string | number; // Body horizontal padding on mobile (xs breakpoint)
    // Transaction status step redirect: when enabled, redirect to {redirect_url} from transaction or custom URL
    errorStepRedirectEnabled?: boolean;
    errorStepRedirectCustomUrl?: string;
    successStepRedirectEnabled?: boolean;
    successStepRedirectCustomUrl?: string;
    processingStepRedirectEnabled?: boolean;
    processingStepRedirectCustomUrl?: string;
    /** When true, the published page emits PAGE_OPENED/PAGE_LOADED/PAGE_CLOSED to the payment page socket (requires transaction_id). */
    paymentPageEventsEnabled?: boolean;
    /** Microsoft Clarity: clarity('set', tagKey, value). tagKey is literal; valueVariable resolves from transaction. Legacy rows may use tagKeyVariable for tag key from transaction. */
    clarityTagRules?: Array<{
      id?: string;
      tagKey?: string;
      valueVariable: string;
      tagKeyVariable?: string;
    }>;
  };
  elements: FormElement[];
  steps?: FormStep[];
  errorStepId?: number | null; // Step ID to show for error statuses
  successStepId?: number | null; // Step ID to show for success statuses
  processingStepId?: number | null; // Step ID to show for processing statuses
  transactionStatusCheck?: {
    enabled: boolean;
    checkIntervalSeconds: number; // How often to check transaction status (in seconds)
  };
  publishedPath?: string | null; // Folder path when published (e.g. pago/form1)
  deployedBuildVersion?: number | null;
  buildVersions?: Array<{
    version: number;
    createdAt: string;
    publicPath: string;
  }> | null;
  buildVersionRules?: Array<{
    id: string;
    variableSource?: 'db' | 'url';
    variable: string;
    value: string;
    contains: boolean;
    buildVersion: number;
  }> | null;
  /** Saved Clarity tag rules (replaces inline styles.clarityTagRules when set). */
  clarityConfigurationId?: number | null;
  createdAt: string;
  updatedAt: string;
}

/** Response from GET /forms/published?path=... (public); includes form, external API, and component styles. */
export interface PublishedFormData {
  form: Form;
  externalApi: FormExternalApi | null;
  componentStyles: ComponentStyle[];
  selectedBuildVersion?: number | null;
  deployedBuildVersion?: number | null;
}

export interface CreateFormDto {
  name: string;
  description?: string;
  previewImageUrl?: string | null;
  formStyleId?: number;
  bodyStyleId?: number;
  messagesStyleId?: number | null;
  customCss?: string;
  customJs?: string;
  styles?: {
    viewMode?: 'single_page' | 'step_by_step';
    stepAnimation?: 'fade' | 'slide' | 'none';
  };
  elements?: FormElement[];
  steps?: FormStep[];
  errorStepId?: number | null;
  successStepId?: number | null;
  processingStepId?: number | null;
  transactionStatusCheck?: {
    enabled: boolean;
    checkIntervalSeconds: number;
  };
  buildVersionRules?: Array<{
    id: string;
    variableSource?: 'db' | 'url';
    variable: string;
    value: string;
    contains: boolean;
    buildVersion: number;
  }> | null;
}

export const formsApi = {
  getAll: async (): Promise<Form[]> => {
    const response = await api.get<Form[]>('/forms');
    return response.data;
  },

  getById: async (id: number): Promise<Form> => {
    const response = await api.get<Form>(`/forms/${id}`);
    return response.data;
  },

  create: async (data: CreateFormDto): Promise<Form> => {
    console.log('formsApi.create called with data:', data);
    try {
      const response = await api.post<Form>('/forms', data);
      console.log('formsApi.create response:', response);
      return response.data;
    } catch (error) {
      console.error('formsApi.create error:', error);
      throw error;
    }
  },

  update: async (id: number, data: Partial<CreateFormDto>): Promise<Form> => {
    console.log('formsApi.update called with id:', id, 'data:', data);
    try {
      const response = await api.patch<Form>(`/forms/${id}`, data);
      console.log('formsApi.update response:', response);
      return response.data;
    } catch (error) {
      console.error('formsApi.update error:', error);
      throw error;
    }
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/forms/${id}`);
  },

  getByPublishedPath: async (
    path: string,
    hashId?: string,
    urlParams?: Record<string, string>,
  ): Promise<PublishedFormData> => {
    const response = await api.get<PublishedFormData>('/forms/published', {
      params: {
        path,
        ...(hashId ? { hash_id: hashId } : {}),
        ...(urlParams || {}),
      },
    });
    return response.data;
  },

  publish: async (formId: number, folderPath: string): Promise<{ publishedPath: string; url: string; version: number }> => {
    const response = await api.post<{ publishedPath: string; url: string; version: number }>(`/forms/${formId}/publish`, {
      folderPath,
    });
    return response.data;
  },

  deployBuildVersion: async (
    formId: number,
    version: number,
  ): Promise<{ publishedPath: string; url: string; deployedVersion: number }> => {
    const response = await api.post<{ publishedPath: string; url: string; deployedVersion: number }>(
      `/forms/${formId}/deploy-build-version`,
      { version },
    );
    return response.data;
  },

  getBuildSnapshot: async (
    formId: number,
    version: number,
  ): Promise<PublishedFormData & { version: number }> => {
    const response = await api.get<PublishedFormData & { version: number }>(
      `/forms/${formId}/build-snapshot/${version}`,
    );
    return response.data;
  },

  syncBuildSnapshot: async (formId: number, version: number): Promise<{ version: number; ok: true }> => {
    const response = await api.post<{ version: number; ok: true }>(
      `/forms/${formId}/build-snapshot/${version}/sync`,
    );
    return response.data;
  },

  unpublish: async (formId: number): Promise<{ publishedPath: null }> => {
    const response = await api.post<{ publishedPath: null }>(`/forms/${formId}/unpublish`);
    return response.data;
  },

  duplicate: async (formId: number): Promise<Form> => {
    const response = await api.post<Form>(`/forms/${formId}/duplicate`);
    return response.data;
  },
};

export interface CreateValidationDto {
  name: string;
  description?: string;
  type?: ValidationType;
  message?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- validation config is a free-form bag
  config?: Record<string, any>;
}

export const validationsApi = {
  getAll: async (): Promise<Validation[]> => {
    const response = await api.get<Validation[]>('/validations');
    return response.data;
  },

  getById: async (id: number): Promise<Validation> => {
    const response = await api.get<Validation>(`/validations/${id}`);
    return response.data;
  },

  create: async (data: CreateValidationDto): Promise<Validation> => {
    const response = await api.post<Validation>('/validations', data);
    return response.data;
  },

  update: async (id: number, data: Partial<CreateValidationDto>): Promise<Validation> => {
    const response = await api.patch<Validation>(`/validations/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/validations/${id}`);
  },
};

export interface ClarityTagRulePayload {
  id?: string;
  tagKey?: string;
  valueVariable: string;
  tagKeyVariable?: string;
}

export interface ClarityConfiguration {
  id: number;
  name: string;
  description?: string | null;
  rules: ClarityTagRulePayload[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateClarityConfigurationDto {
  name: string;
  description?: string | null;
  rules?: ClarityTagRulePayload[] | null;
}

export const clarityConfigurationsApi = {
  getAll: async (): Promise<ClarityConfiguration[]> => {
    const response = await api.get<ClarityConfiguration[]>('/clarity-configurations');
    return response.data;
  },

  getById: async (id: number): Promise<ClarityConfiguration> => {
    const response = await api.get<ClarityConfiguration>(`/clarity-configurations/${id}`);
    return response.data;
  },

  create: async (data: CreateClarityConfigurationDto): Promise<ClarityConfiguration> => {
    const response = await api.post<ClarityConfiguration>('/clarity-configurations', data);
    return response.data;
  },

  update: async (id: number, data: Partial<CreateClarityConfigurationDto>): Promise<ClarityConfiguration> => {
    const response = await api.patch<ClarityConfiguration>(`/clarity-configurations/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/clarity-configurations/${id}`);
  },
};

export interface UploadImageResponse {
  url: string;
  filename: string;
  originalName: string;
  size: number;
  mimetype: string;
}

export const uploadsApi = {
  uploadImage: async (file: File): Promise<UploadImageResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post<UploadImageResponse>('/uploads/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export type ComponentType = 'input' | 'button' | 'text' | 'image' | 'list' | 'text_with_value' | 'timer' | 'copy_section' | 'link' | 'upload_file' | 'step_progress_bar' | 'modal' | 'notification_message' | 'container' | 'processing_status' | 'slideshow' | 'loader' | 'svg' | 'qr_code' | 'form' | 'step' | 'body' | 'messages';

export interface ComponentStyle {
  id: number;
  name: string;
  description?: string;
  componentType: ComponentType;
  groupId?: number | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- component style CSS bag
  styles: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface ComponentStyleGroup {
  id: number;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateComponentStyleDto {
  name: string;
  description?: string;
  componentType: ComponentType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- component style CSS bag
  styles?: Record<string, any>;
  groupId?: number | null;
}

export const componentStylesApi = {
  getAll: async (opts?: { componentType?: ComponentType; groupId?: number }): Promise<ComponentStyle[]> => {
    const params: Record<string, string | number> = {};
    if (opts?.componentType) params.componentType = opts.componentType;
    if (opts?.groupId != null) params.groupId = opts.groupId;
    const response = await api.get<ComponentStyle[]>('/component-styles', { params });
    return response.data;
  },

  getById: async (id: number): Promise<ComponentStyle> => {
    const response = await api.get<ComponentStyle>(`/component-styles/${id}`);
    return response.data;
  },

  create: async (data: CreateComponentStyleDto): Promise<ComponentStyle> => {
    const response = await api.post<ComponentStyle>('/component-styles', data);
    return response.data;
  },

  update: async (id: number, data: Partial<CreateComponentStyleDto>): Promise<ComponentStyle> => {
    const response = await api.patch<ComponentStyle>(`/component-styles/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/component-styles/${id}`);
  },
};

export const componentStyleGroupsApi = {
  getAll: async (): Promise<ComponentStyleGroup[]> => {
    const response = await api.get<ComponentStyleGroup[]>('/component-style-groups');
    return response.data;
  },

  getById: async (id: number): Promise<ComponentStyleGroup> => {
    const response = await api.get<ComponentStyleGroup>(`/component-style-groups/${id}`);
    return response.data;
  },

  getStyles: async (groupId: number): Promise<ComponentStyle[]> => {
    const response = await api.get<ComponentStyle[]>(`/component-style-groups/${groupId}/styles`);
    return response.data;
  },

  create: async (data: { name: string; description?: string }): Promise<ComponentStyleGroup> => {
    const response = await api.post<ComponentStyleGroup>('/component-style-groups', data);
    return response.data;
  },

  update: async (id: number, data: { name?: string; description?: string }): Promise<ComponentStyleGroup> => {
    const response = await api.patch<ComponentStyleGroup>(`/component-style-groups/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/component-style-groups/${id}`);
  },
};

export const settingsApi = {
  getHiddenTransactionVariables: async (): Promise<string[]> => {
    const response = await api.get<{ hiddenVariables: string[] }>(
      '/settings/transaction-variables',
    );
    return response.data.hiddenVariables;
  },

  updateHiddenTransactionVariables: async (
    hiddenVariables: string[],
  ): Promise<string[]> => {
    const response = await api.patch<{ hiddenVariables: string[] }>(
      '/settings/transaction-variables',
      { hiddenVariables },
    );
    return response.data.hiddenVariables;
  },
};

export const transactionsApi = {
  getColumns: async (): Promise<string[]> => {
    const response = await api.get<{ columns: string[] }>('/transactions/columns');
    return response.data.columns;
  },

  getByHashId: async (hashId: string): Promise<Record<string, unknown>> => {
    // Encode hash_id to handle special characters like +, /, =
    const encodedHashId = encodeURIComponent(hashId);
    const response = await api.get<Record<string, unknown>>(`/transactions/by-hash-id?hash_id=${encodedHashId}`);
    return response.data;
  },
  getStatusByHashId: async (hashId: string): Promise<{ status: string }> => {
    // Encode hash_id to handle special characters like +, /, =
    const encodedHashId = encodeURIComponent(hashId);
    const response = await api.get<{ status: string }>(`/transactions/status-by-hash-id?hash_id=${encodedHashId}`);
    return response.data;
  },

  valueExists: async (
    column: string,
    value: string,
    currency?: string,
  ): Promise<{ exists: boolean }> => {
    const response = await api.get<{ exists: boolean }>('/transactions/value-exists', {
      params: {
        column,
        value,
        ...(currency ? { currency } : {}),
      },
    });
    return response.data;
  },
};

export const payoutsApi = {
  getByHashId: async (hashId: string): Promise<Record<string, unknown>> => {
    const encodedHashId = encodeURIComponent(hashId);
    const response = await api.get<Record<string, unknown>>(`/payouts/by-hash-id?hash_id=${encodedHashId}`);
    return response.data;
  },
  getStatusByHashId: async (hashId: string): Promise<{ status: string }> => {
    const encodedHashId = encodeURIComponent(hashId);
    const response = await api.get<{ status: string }>(`/payouts/status-by-hash-id?hash_id=${encodedHashId}`);
    return response.data;
  },
};

export interface FormExternalApi {
  id: number;
  formId: number;
  enabled: boolean;
  /** Null on public/published responses; used only server-side for start-transaction. */
  externalApiUrl: string | null;
  /** Null on public/published responses; used only server-side for start-transaction. */
  apiKey: string | null;
  triggerButtonElementId: number | null;
  triggerButtonElementIds?: number[];
  disableButtonAfterSubmitElementIds?: number[];
  fieldMappings: {
    simpleFields?: Array<{
      paramName: string;
      inputName: string;
      disableInputAfterSubmit?: boolean;
      notificationText?: string;
      notificationIcon?: string;
    }>;
    arrayFields?: Array<{ paramName: string; inputMappings: Array<{ key: string; inputName: string }> }>;
  } | null;
}

export interface CreateFormExternalApiDto {
  formId: number;
  enabled?: boolean;
  externalApiUrl?: string | null;
  apiKey?: string | null;
  triggerButtonElementId?: number | null;
  triggerButtonElementIds?: number[];
  disableButtonAfterSubmitElementIds?: number[];
  fieldMappings?: {
    simpleFields?: Array<{
      paramName: string;
      inputName: string;
      disableInputAfterSubmit?: boolean;
      notificationText?: string;
      notificationIcon?: string;
    }>;
    arrayFields?: Array<{ paramName: string; inputMappings: Array<{ key: string; inputName: string }> }>;
  } | null;
}

export const formsExternalApi = {
  getByFormId: async (formId: number): Promise<FormExternalApi | null> => {
    const response = await api.get<FormExternalApi | null>(`/forms/${formId}/external-api`);
    return response.data;
  },
  createOrUpdate: async (formId: number, data: CreateFormExternalApiDto): Promise<FormExternalApi> => {
    const response = await api.post<FormExternalApi>(`/forms/${formId}/external-api`, data);
    return response.data;
  },
  update: async (formId: number, data: Partial<CreateFormExternalApiDto>): Promise<FormExternalApi> => {
    const response = await api.patch<FormExternalApi>(`/forms/${formId}/external-api`, data);
    return response.data;
  },
  startTransaction: async (
    formId: number,
    transactionId: string,
    formValues: Record<string, unknown>,
    idempotencyKey?: string,
  ): Promise<unknown> => {
    const response = await api.post(`/forms/${formId}/start-transaction`, {
      transactionId,
      formValues,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    });
    return response.data;
  },
  completePayout: async (
    formId: number,
    payoutId: string,
    formValues: Record<string, unknown>,
    idempotencyKey?: string,
  ): Promise<unknown> => {
    const response = await api.post(`/forms/${formId}/complete-payout`, {
      payoutId,
      formValues,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    });
    return response.data;
  },
  /** Best-effort HTTP track when socket is unavailable (core-v1 admin/payment-page/track-action). */
  trackPaymentPageAction: async (
    formId: number,
    payload: {
      action: string;
      hashedId: string;
      deviceUuid?: string;
      pageVersion?: number;
      actionData?: Record<string, unknown>;
    },
  ): Promise<{ success: boolean }> => {
    const response = await api.post<{ success: boolean }>(
      `/forms/${formId}/payment-page/track-action`,
      payload,
    );
    return response.data;
  },
};

export type FormStatisticsSegment = 'all' | 'ftd' | 'std';
export type FormStatisticsBucket = 'hour' | 'day';

export interface FormStatisticsMerchant {
  id: number;
  name: string | null;
  currency: string | null;
}

export interface FormStatisticsDashboard {
  range: { from: string; to: string; matureMin: number; bucket: string };
  form: { id: number; name: string; versions: number[] };
  merchant: { id: number; name: string | null };
  kpis: {
    assigned: number;
    finished: number;
    paid: number;
    failed: number;
    others: number;
    conversionPct: number | null;
    matureFinished: number;
    maturePaid: number;
    matureConversionPct: number | null;
    immature: number;
    revenue: number;
    avgFinalizationMin: number | null;
    loaded: number;
    sockFail: number;
    disputes: number;
  };
  series: Array<{
    t: string;
    success: number;
    failed: number;
    others: number;
    total: number;
    conversionPct: number | null;
  }>;
  byVersion: Array<{
    version: string;
    assigned: number;
    finished: number;
    paid: number;
    conversionPct: number | null;
    matureConversionPct: number | null;
  }>;
  funnel: Array<{ step: string; n: number }>;
}

export const formStatisticsApi = {
  listMerchants: async (): Promise<FormStatisticsMerchant[]> => {
    const response = await api.get<FormStatisticsMerchant[]>('/merchants/p2p');
    return response.data;
  },

  getDashboard: async (
    formId: number,
    params: {
      from: string;
      to: string;
      merchantId: number;
      currency?: string;
      segment?: FormStatisticsSegment;
      bucket?: FormStatisticsBucket;
      matureMin?: number;
    },
  ): Promise<FormStatisticsDashboard> => {
    const response = await api.get<FormStatisticsDashboard>(`/forms/${formId}/statistics`, {
      params,
    });
    return response.data;
  },
};

export default api;
